import '../style.css';
import * as Config from './core/config.js';
import * as Renderer from './rendering/renderer.js';
import { InputManager } from './ui/InputManager.js';
import { EventBus, EVENTS } from './services/EventBus.js';
import { AppContext } from './core/AppContext.js';
import { UIManager } from './ui/UIManager.js';
import { Application } from './core/Application.js';
import { SettingsLoader } from './services/SettingsLoader.js';
import * as PersistenceService from './services/PersistenceService.js';
import { describeRuleset } from './core/rulesetDescriptor.js';
import { createGpuHelpPanel, detectGraphicsPath } from './utils/gpuSupport.js';
import rulesetLibrary from './core/library/rulesets.json';
import patternLibrary from './core/library/patterns.json';
import { APP_VERSION } from './version.js';

console.info(`HexLife Explorer build ${APP_VERSION}`);

let gl;
let appContext;
let uiManager;
let initializedWorkerCount = 0;

function updateLoadingStatus(message) {
    const statusElement = document.getElementById('loading-status');
    if (statusElement) {
        statusElement.textContent = message;
    }
}

/**
 * Replace the loading screen's spinner text with the "how to turn acceleration on" panel.
 *
 * The old copy was one sentence telling people to change a setting it did not name, in a menu it
 * did not locate — which for most readers is indistinguishable from "it's broken". Detection has
 * always been right here; only the answer was missing.
 */
function showGpuHelp(detection) {
    updateLoadingStatus('');
    const host = document.getElementById('loading-status')?.parentElement;
    if (!host) return;
    document.getElementById('loading-indicator')?.classList.add('gpu-blocked');
    // "Loading HexLife Explorer..." over an explanation of why it will never load is its own small
    // lie — nothing is in progress here.
    const title = document.getElementById('loading-title');
    if (title) title.textContent = 'HexLife Explorer';
    host.append(createGpuHelpPanel({
        status: detection.status === 'no-webgl2' ? 'no-webgl2' : 'software',
        reloadHint: 'Then reload this page.',
    }));
}

async function initialize() {
    console.log("Initializing...");
    updateLoadingStatus("Parsing configuration...");
    const canvas = document.getElementById('hexGridCanvas');
    if (!canvas) {
        console.error("Canvas element not found!");
        return;
    }

    // GPU acceleration detection
    updateLoadingStatus("Checking GPU acceleration...");
    const detection = detectGraphicsPath();
    if (detection.status === 'no-webgl2' || detection.status === 'software') {
        console.error("GPU acceleration not detected:", detection);
        showGpuHelp(detection);
        return;
    }
    console.log("GPU detection:", detection);

    // Capture dev-tool flags BEFORE loadFromUrl, which may strip query params via replaceState.
    const curateMode = new URLSearchParams(window.location.search).has('curate');
    const corpusMode = new URLSearchParams(window.location.search).has('corpus');
    const benchmarkMode = import.meta.env.DEV
        && new URLSearchParams(window.location.search).has('benchmark');

    const sharedSettings = SettingsLoader.loadFromUrl();

    // Resolve the grid size before constructing anything that depends on grid dimensions (workers,
    // renderer buffers). A size from a share URL wins and is persisted; otherwise use the saved
    // setting, falling back to the default preset.
    const defaultGridRows = Config.GRID_SIZE_PRESETS[Config.DEFAULT_GRID_SIZE_KEY];
    let gridRows;
    if (sharedSettings.fromUrl && sharedSettings.gridRows) {
        gridRows = sharedSettings.gridRows;
        PersistenceService.saveUISetting('gridRows', gridRows);
    } else {
        gridRows = PersistenceService.loadUISetting('gridRows', defaultGridRows);
    }
    Config.setGridDimensions(gridRows);
    console.log(`Grid size: ${Config.GRID_ROWS} rows x ${Config.GRID_COLS} cols (${Config.NUM_CELLS} cells).`);

    // `?edit=1` (Devvit "what ruleset is this?" deep link): pre-seed the editor mode that fits the
    // linked rule *before* the UI constructs the editor, so it opens already showing the rule in
    // its natural representation. Like `gridRows` above, a URL that asks for it wins and persists.
    if (sharedSettings.fromUrl && sharedSettings.openRulesetEditor && sharedSettings.rulesetHex) {
        const desc = describeRuleset(sharedSettings.rulesetHex);
        if (desc) {
            const mode = desc.type === 'n-count' ? 'neighborCount'
                : desc.type === 'r-sym' ? 'rotationalSymmetry'
                : 'detailed'; // raw: only the 128-cell grid tells the truth (mobile still downgrades)
            PersistenceService.saveUISetting('rulesetEditorMode', mode);
        }
    }

    const libraryData = { rulesets: rulesetLibrary, patterns: patternLibrary };
    
    updateLoadingStatus("Spooling up simulation workers...");
    appContext = new AppContext(sharedSettings, libraryData);
    
    updateLoadingStatus("Initializing rendering engine...");
    gl = await Renderer.initRenderer(canvas, appContext);
    if (!gl) {
        console.error("Renderer initialization failed.");
        updateLoadingStatus("Error: WebGL2 not supported.");
        return;
    }
    uiManager = new UIManager(appContext);
    new InputManager(canvas, appContext.worldManager, appContext, uiManager.isMobile());

    EventBus.subscribe(EVENTS.WORKER_INITIALIZED, ({ worldIndex }) => {
        const hexElement = document.getElementById(`loader-hex-${worldIndex}`);
        if (hexElement) {
            hexElement.classList.add('active');
        }
        initializedWorkerCount++;
        updateLoadingStatus(`Spooling up simulation workers... (${initializedWorkerCount}/${Config.NUM_WORLDS})`);
    });

    EventBus.dispatch(EVENTS.SELECTED_WORLD_CHANGED, appContext.worldManager.getSelectedWorldIndex());
    EventBus.dispatch(EVENTS.SIMULATION_PAUSED, appContext.simulationController.getIsPaused());
    window.addEventListener('beforeunload', () => {
        uiManager.destroy();
        appContext.worldManager.terminateAllWorkers();
    });

    const app = new Application(appContext);
    if (window.__headless) {
        window.__hexlife = appContext; // headless-only debug handle for in-browser verification
        // #40 gates: `spacetimeBench({...})` times the ray-march, `spacetimeInfo()` reports what the
        // volume holds so it can be checked against the flat view. Attached only here, so
        // `initRenderer` stays clear of the spacetime path entirely.
        appContext.spacetimeBench = Renderer.runSpacetimeBenchmark;
        appContext.spacetimeInfo = Renderer.getSpacetimeInfo;
    }
    if (benchmarkMode) appContext.suppressAutoTour = true;
    app.run();

    // Second half of `?edit=1`: open the editor where this device shows it — draggable panel on
    // desktop, Build ▸ Editor view on mobile (COMMAND_TOGGLE_PANEL deliberately no-ops there).
    // `suppressAutoTour` (read by Application's loading-complete hook) keeps the first-visit core
    // tour from auto-starting this session — its opening step resets the UI, which would close the
    // editor this link promised. The tour is not marked completed; a normal visit still offers it.
    if (sharedSettings.fromUrl && sharedSettings.openRulesetEditor) {
        appContext.suppressAutoTour = true;
        if (uiManager.isMobile()) {
            EventBus.dispatch(EVENTS.COMMAND_SHOW_MOBILE_VIEW, { targetView: 'build', segment: 'editor' });
        } else {
            EventBus.dispatch(EVENTS.COMMAND_TOGGLE_PANEL, { panelName: 'ruleset', show: true });
        }
    }

    // DEV-only: library IC curation tool. Opened with `?curate=1`; lazily imported so it never ships
    // in a normal session's hot path. Waits for the workers (the bake engine borrows a live world).
    if (curateMode) {
        import('./ui/dev/LibraryCurationOverlay.js')
            .then(({ LibraryCurationOverlay }) => {
                appContext._curationOverlay = new LibraryCurationOverlay(appContext);
            })
            .catch(err => console.error('Failed to load curation overlay:', err));
    }

    // OWNER-only: Corpus v1 collection tool (#37 Stage 4B.2). Opened with `?corpus=1`; lazily
    // imported so it never ships in a normal session's hot path. It drives all nine worlds and
    // brackets the session with the auto-explore snapshot/restore pair, so it must mount after the
    // workers exist.
    if (corpusMode) {
        import('./ui/dev/CorpusLabOverlay.js')
            .then(({ CorpusLabOverlay }) => {
                appContext._corpusLab = new CorpusLabOverlay(appContext);
            })
            .catch(err => console.error('Failed to load Corpus Lab overlay:', err));
    }

    // DEV-only: deterministic #37 benchmark capture surface. The browser runner uses a real live
    // worker per world and publishes the generated JSON into the page for saving; normal sessions
    // never load this module.
    if (benchmarkMode) {
        import('./ui/dev/InterestingnessBenchmarkCapture.js')
            .then(({ mountInterestingnessBenchmarkCapture }) => {
                mountInterestingnessBenchmarkCapture(appContext, rulesetLibrary);
            })
            .catch(err => console.error('Failed to load benchmark capture surface:', err));
    }
}

initialize().catch(err => {
    console.error("Initialization failed:", err);
    updateLoadingStatus("Error during initialization. See console for details.");
    alert("Application failed to initialize. See console for details.");
});
